import React, { useState, useRef, useEffect } from 'react';
import { Stack, Title, Text, Paper, Accordion, Group, Box, Button, Collapse, Badge, Timeline, ThemeIcon, List, Divider, useMantineTheme } from '@mantine/core';
import { IconChevronDown, IconChevronRight, IconPlayerPlay, IconCheck, IconInfoCircle, IconFlag, IconBook, IconPencil, IconTarget, IconFlask, IconLink, IconListCheck, IconSchool } from '@tabler/icons-react';
import { StoredCourse } from '../services/courseService';
import { FAQ, CourseContent as CourseContentType, CourseState, TodoItem } from '../types';
import TodoList from './TodoList';
import { notifications } from '@mantine/notifications';
import axios from 'axios';
import { API_CONFIG } from '../../../config/constants';
import { courseService } from '../services/courseService';
import { useCourse } from '../context/CourseContext';
import CourseLayout from './CourseLayout';

interface CourseContentDisplayProps {
  content: StoredCourse;
  isNestedModule?: boolean;
  onStateChange?: (newState: CourseState) => void;
}

interface ModuleContentProps {
  moduleContent: CourseContentType;
  isLoading: boolean;
}

const CourseStateIndicator: React.FC<{ state: CourseState }> = ({ state }) => {
  const getStateColor = (state: CourseState) => {
    switch (state) {
      case 'not_started':
        return 'gray';
      case 'in_progress':
        return 'blue';
      case 'completed':
        return 'green';
      default:
        return 'gray';
    }
  };

  const getStateLabel = (state: CourseState) => {
    switch (state) {
      case 'not_started':
        return 'Not Started';
      case 'in_progress':
        return 'In Progress';
      case 'completed':
        return 'Completed';
      default:
        return 'Unknown';
    }
  };

  return (
    <Badge 
      color={getStateColor(state)}
      variant="filled"
      size="lg"
      radius="md"
    >
      {getStateLabel(state)}
    </Badge>
  );
};

const ModuleContent: React.FC<ModuleContentProps> = ({ moduleContent, isLoading }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const theme = useMantineTheme();

  const faqItems = moduleContent.faqs || [];

  const renderStructuredContent = () => {
    // Parse the content string into sections
    const sections = (moduleContent.content || '').split('\n\n').reduce((acc: { [key: string]: string[] }, section) => {
      const lines = section.trim().split('\n');
      if (lines.length > 0) {
        const title = lines[0].replace(':', '').trim();
        const content = lines.slice(1)
          .map(line => line.trim())
          .filter(line => line && !line.includes(':'));
        if (content.length > 0) {
          acc[title] = content;
        }
      }
      return acc;
    }, {});

    // Define the essential sections with their configurations
    const moduleSteps = [
      {
        title: "Internet Learning Resources",
        key: "Learning Resources",
        description: "Curated online resources you can access to support your learning",
        icon: <IconLink size={20} />,
        content: sections["Learning Resources"] || sections["Key Learning Resources"] || [],
        color: "blue"
      },
      {
        title: "Learning Blindspots",
        key: "learningHurdles",
        description: "Common misconceptions and challenges you should be aware of",
        icon: <IconFlag size={20} />,
        content: moduleContent.learningHurdles || sections["Learning Hurdles"] || sections["Common Misconceptions"] || [],
        color: "orange"
      },
      {
        title: "Practical Actions",
        key: "realWorldActions",
        description: "Activities to complete that demonstrate your progress",
        icon: <IconListCheck size={20} />,
        content: moduleContent.realWorldActions || sections["Real World Actions"] || [],
        color: "green"
      },
      {
        title: "Project Assessment",
        key: "assignment",
        description: "Final project to demonstrate your mastery of the content",
        icon: <IconSchool size={20} />,
        content: moduleContent.assignment ? [moduleContent.assignment] : [],
        color: "violet"
      }
    ];

    return (
      <Stack spacing="xl">
        <Box mb="md">
          <Group spacing="xs" mb="sm">
            <ThemeIcon radius="xl" size={28} color="indigo">
              <IconTarget size={20} />
            </ThemeIcon>
            <Text size="lg" weight={500}>Learning Objectives</Text>
          </Group>
          <Text size="sm" color="dimmed">What you'll be able to do after completing this module:</Text>
          <List spacing="sm" size="sm" mt="xs">
            {(sections["Key Learning Objectives"] || []).map((item, i) => (
              <List.Item key={i} icon={
                <ThemeIcon color="indigo" size={20} radius="xl">
                  <IconCheck size={14} />
                </ThemeIcon>
              }>
                {item}
              </List.Item>
            ))}
          </List>
        </Box>

        <Accordion 
          multiple 
          defaultValue={['Learning Resources', 'learningHurdles', 'realWorldActions']}
          styles={{
            item: {
              borderRadius: theme.radius.md,
              border: `1px solid ${theme.colors.gray[3]}`,
              marginBottom: theme.spacing.sm,
              '&:first-of-type': {
                borderTop: `1px solid ${theme.colors.gray[3]}`,
              },
            },
            control: {
              borderRadius: theme.radius.md,
            },
          }}
        >
          {moduleSteps
            .filter(step => step.content && step.content.length > 0)
            .map((step, index) => (
              <Accordion.Item key={index} value={step.key}>
                <Accordion.Control icon={
                  <ThemeIcon radius="xl" size={28} color={step.color}>
                    {step.icon}
                  </ThemeIcon>
                }>
                  <Group spacing="xs">
                    <Text weight={500}>{step.title}</Text>
                    <Badge size="sm" variant="filled" color={step.color}>
                      {step.content.length}
                    </Badge>
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack spacing="xs">
                    <Text color="dimmed" size="sm">{step.description}</Text>
                    <List spacing="sm" size="sm" mt="xs">
                      {step.content.map((item, i) => (
                        <List.Item key={i} icon={
                          step.key === "Learning Resources" ? (
                            <ThemeIcon color="blue" size={20} radius="xl">
                              <IconLink size={14} />
                            </ThemeIcon>
                          ) : step.key === "learningHurdles" ? (
                            <ThemeIcon color="orange" size={20} radius="xl">
                              <IconFlag size={14} />
                            </ThemeIcon>
                          ) : step.key === "realWorldActions" ? (
                            <ThemeIcon color="green" size={20} radius="xl">
                              <IconListCheck size={14} />
                            </ThemeIcon>
                          ) : (
                            <ThemeIcon color="violet" size={20} radius="xl">
                              <IconSchool size={14} />
                            </ThemeIcon>
                          )
                        }>
                          {item}
                        </List.Item>
                      ))}
                    </List>
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>
          ))}
        </Accordion>

        {/* FAQs Section */}
        {faqItems && faqItems.length > 0 && (
          <Box>
            <Group spacing="xs" mb="md">
              <ThemeIcon radius="xl" size={28} color="orange">
                <IconInfoCircle size={20} />
              </ThemeIcon>
              <Text size="lg" weight={500}>Frequently Asked Questions</Text>
              <Badge size="sm" variant="filled" color="orange">
                {faqItems.length}
              </Badge>
            </Group>
            <Accordion 
              styles={{
                item: {
                  borderRadius: theme.radius.md,
                  border: `1px solid ${theme.colors.gray[3]}`,
                  marginBottom: theme.spacing.xs,
                  '&:first-of-type': {
                    borderTop: `1px solid ${theme.colors.gray[3]}`,
                  },
                },
                control: {
                  borderRadius: theme.radius.md,
                },
              }}
            >
              {faqItems.map((faq, index) => (
                <Accordion.Item key={index} value={`faq-${index}`}>
                  <Accordion.Control>
                    <Text weight={500}>{faq.question}</Text>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <Text>{faq.answer}</Text>
                  </Accordion.Panel>
                </Accordion.Item>
              ))}
            </Accordion>
          </Box>
        )}
      </Stack>
    );
  };

  return (
    <Paper
      withBorder
      radius="md"
      sx={(theme) => ({
        position: 'relative',
        transition: 'all 200ms ease',
        overflow: 'hidden',
      })}
    >
      <Stack spacing="md">
        <Group 
          position="apart" 
          onClick={() => setIsExpanded(!isExpanded)} 
          sx={{ 
            cursor: 'pointer',
            flexWrap: 'nowrap',
            padding: theme.spacing.md,
            borderBottom: isExpanded ? `1px solid ${theme.colors.gray[2]}` : 'none',
          }}
        >
          <Group spacing="md">
            <ThemeIcon radius="xl" size={32} color="blue">
              <IconSchool size={20} />
            </ThemeIcon>
            <Title order={3}>Learning Materials</Title>
          </Group>
          {isExpanded ? <IconChevronDown size={20} /> : <IconChevronRight size={20} />}
        </Group>

        <Collapse in={isExpanded}>
          <Box p="md">
            {renderStructuredContent()}
          </Box>
        </Collapse>
      </Stack>
    </Paper>
  );
};

const CourseContentDisplay: React.FC<CourseContentDisplayProps> = ({ content, isNestedModule = false, onStateChange }) => {
  const { content: courseContent, input } = content;
  const [loading, setLoading] = useState(false);
  const firstModuleRef = useRef<HTMLDivElement>(null);
  const { triggerRefresh } = useCourse();
  const [currentState, setCurrentState] = useState<CourseState>(content.state || 'not_started');

  // Update currentState when content prop changes
  useEffect(() => {
    if (content.state !== currentState) {
      setCurrentState(content.state || 'not_started');
    }
  }, [content.state]);

  const handleStartLearning = async () => {
    try {
      setLoading(true);
      await courseService.updateCourseState(content.id, 'in_progress', new Date());
      
      setCurrentState('in_progress');
      content.state = 'in_progress';
      content.startedAt = new Date();
      
      triggerRefresh();
      onStateChange?.('in_progress');
      
      firstModuleRef.current?.scrollIntoView({ behavior: 'smooth' });
      
      notifications.show({
        title: 'Course Started',
        message: 'Good luck with your learning journey!',
        color: 'green'
      });
    } catch (error) {
      console.error('Error starting course:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to start the course. Please try again.',
        color: 'red'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteCourse = async () => {
    try {
      setLoading(true);
      await courseService.updateCourseState(content.id, 'completed');
      
      // Update local state
      setCurrentState('completed');
      content.state = 'completed';
      
      // Trigger course history refresh
      triggerRefresh();
      
      // Notify parent component if provided
      onStateChange?.('completed');
      
      notifications.show({
        title: 'Congratulations!',
        message: 'You have completed this course.',
        color: 'green',
        icon: <IconCheck />
      });
    } catch (error) {
      console.error('Error completing course:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to mark course as completed. Please try again.',
        color: 'red'
      });
    } finally {
      setLoading(false);
    }
  };

  // Helper function to generate todo items
  const generateTodoItems = (moduleContent: CourseContentType): TodoItem[] => {
    const todos: TodoItem[] = [];
    let id = 1;

    // Add real-world actions
    moduleContent.realWorldActions.forEach(action => {
      todos.push({
        id: `action-${id++}`,
        text: action,
        status: 'not_started',
        category: 'action',
        notes: ''
      });
    });

    // Add assignment as a todo item
    if (moduleContent.assignment) {
      todos.push({
        id: `assignment-${id++}`,
        text: `Complete the project: ${moduleContent.assignment}`,
        status: 'not_started',
        category: 'assignment',
        notes: ''
      });
    }

    return todos;
  };

  // For nested display mode
  if (isNestedModule) {
    return (
      <Stack spacing="md">
        <CourseLayout
          courseId={content.id}
          moduleContent={courseContent}
          courseState={currentState}
        >
          <ModuleContent
            moduleContent={courseContent}
            isLoading={loading}
          />
        </CourseLayout>
      </Stack>
    );
  }

  return (
    <Stack spacing="xl">
      {currentState === 'not_started' && (
        <Paper p="md" withBorder>
          <Stack spacing="md" align="center">
            <Title order={3}>Ready to Start Learning?</Title>
            <Text>
              You've generated this course content. When you're ready to begin, click the 'Start Learning' button.
            </Text>
            <Button
              size="lg"
              onClick={handleStartLearning}
              loading={loading}
            >
              Start Learning
            </Button>
          </Stack>
        </Paper>
      )}

      {currentState !== 'not_started' && (
        <CourseLayout
          courseId={content.id}
          moduleContent={courseContent}
          courseState={currentState}
          isOpen={true}
        >
          <ModuleContent
            key={content.id}
            moduleContent={courseContent}
            isLoading={loading}
          />
        </CourseLayout>
      )}

      {currentState === 'in_progress' && (
        <Paper p="md" withBorder>
          <Stack spacing="md" align="center">
            <Title order={3}>Complete Course</Title>
            <Text>
              When you've completed all the learning content, click the 'Mark as Completed' button.
            </Text>
            <Button
              variant="outline"
              color="green"
              size="lg"
              onClick={handleCompleteCourse}
              loading={loading}
            >
              Mark as Completed
            </Button>
          </Stack>
        </Paper>
      )}
    </Stack>
  );
};

export default CourseContentDisplay; 